import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { QueryKeys } from "queryClient";
import {
  CalcTaskPage,
  getCalcTaskList,
  getCalcTaskListByExpansion,
} from "modules/calctasks/api";
import { useCalcTaskListFilterAndSort } from "../useCalcTaskListFilterAndSort";

type CalcTaskListParamsType = {
  expansionID: string;
};

/**
 * Seitenparameter im Cache. loadAll steckt bewusst mit drin: bei einer
 * Invalidierung lädt TanStack jede Seite mit ihrem Parameter neu, und eine per
 * "Alle laden" geholte Restseite soll dann wieder vollständig sein statt auf 50
 * Zeilen zusammenzufallen.
 */
interface PageParam {
  token: string | null;
  loadAll: boolean;
}

type CalcTaskData = InfiniteData<CalcTaskPage, PageParam>;

/**
 * Laden-Preisberechnungen seitenweise (50 je Seite, neueste zuerst), optional
 * auf die Edition aus der Route eingeschränkt. Gleiches Muster wie
 * useCardmarketCalcTaskList: Filter und Sortierung laufen clientseitig über die
 * geladenen Seiten, "Alle laden" holt den Rest in einem Zug.
 */
export const useCalcTaskList = () => {
  const { expansionID } = useParams<CalcTaskListParamsType>();
  const queryClient = useQueryClient();

  const filterAndSortCalcTasks = useCalcTaskListFilterAndSort();

  // Die Edition gehört in den Schlüssel; die Invalidierung über das Präfix
  // [CalcTasks] trifft weiterhin beide Varianten.
  const queryKey = useMemo(
    () => [QueryKeys.CalcTasks, expansionID ?? "all"],
    [expansionID],
  );

  const fetchPage = useCallback(
    ({ token, loadAll }: PageParam) =>
      expansionID
        ? getCalcTaskListByExpansion(expansionID, token, loadAll)
        : getCalcTaskList(token, loadAll),
    [expansionID],
  );

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery<
      CalcTaskPage,
      Error,
      CalcTaskData,
      typeof queryKey,
      PageParam
    >({
      queryKey,
      queryFn: ({ pageParam }) => fetchPage(pageParam),
      initialPageParam: { token: null, loadAll: false },
      getNextPageParam: (lastPage) =>
        lastPage.nextToken
          ? { token: lastPage.nextToken, loadAll: false }
          : undefined,
      staleTime: 1000 * 60 * 15, // 15 minutes
      gcTime: 1000 * 60 * 60, // 1 hour
    });

  const [isLoadingAll, setIsLoadingAll] = useState(false);

  const loadMore = useCallback(() => {
    if (!isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetchingNextPage]);

  const loadAll = useCallback(async () => {
    const current = queryClient.getQueryData<CalcTaskData>(queryKey);
    const lastToken = current?.pages.at(-1)?.nextToken ?? null;

    if (!current || !lastToken || isLoadingAll) {
      return;
    }

    setIsLoadingAll(true);

    try {
      const pageParam: PageParam = { token: lastToken, loadAll: true };
      const rest = await fetchPage(pageParam);

      queryClient.setQueryData<CalcTaskData>(queryKey, (old) =>
        old
          ? {
              pages: [...old.pages, rest],
              pageParams: [...old.pageParams, pageParam],
            }
          : old,
      );
    } finally {
      setIsLoadingAll(false);
    }
  }, [fetchPage, isLoadingAll, queryClient, queryKey]);

  const calcTaskList = useMemo(
    () =>
      filterAndSortCalcTasks(data?.pages.flatMap((page) => page.items) ?? []),
    [data, filterAndSortCalcTasks],
  );

  return {
    calcTaskList,
    expansionID,
    isLoading,
    isLoadingMore: isFetchingNextPage || isLoadingAll,
    hasNextData: Boolean(hasNextPage),
    loadMore,
    loadAll,
  };
};
